import {Intervals} from "../../Intervals"

/**
 * Weekly Device Summary Report settings schema.
 *
 * Like System Health, this report has one manual-run setting (the show-offline
 * toggle) plus the template-only delivery schedule. Setting keys (`show_offline`)
 * are unchanged from the pre-schema WeeklyDeviceSummaryReportSettings.vue, so
 * saved templates are unaffected.
 *
 * Note: `slug` is `weekly-device-summary` (no `-report`) to match the POST route
 * the composable builds; the template lookup name `weekly-device-summary-report`
 * is passed separately as GroupReportTemplate's `report-name`.
 */
export default {
    slug: 'weekly-device-summary',
    icon:
        '<path stroke="none" d="M0 0h24v24H0z" fill="none"/>'
        + '<path d="M3 5a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1l0 -10" />'
        + '<path d="M7 20h10" />'
        + '<path d="M9 16v4" />'
        + '<path d="M15 16v4" />'
        + '<path d="M9 12v-4" />'
        + '<path d="M12 12v-1" />'
        + '<path d="M15 12v-2" />',
    permission: 'manage_device_summary_report',
    title: 'Device Summary',
    description: 'One line per device showing how it performed, plus any that went offline.',
    info: 'One row per selected point, grouped by area: minimum, average and maximum reading, threshold and offline alarm counts, percent of time online, and minutes out of range for the period. Sound and diagnostic points are left out.',

    sections: [
        {
            title: 'Data Settings',
            description: 'Choose what appears in the generated report.',
            fields: [
                {
                    key: 'show_offline',
                    type: 'toggle',
                    label: 'Offline Devices',
                    description: 'When on, points with no readings at all during the period are listed as Offline, with their alarm counts. When off, they are left out of the report.',
                },
            ],
        },

        {
            // Template-only: the recurring send schedule. On-demand runs derive
            // the window from the interval, so this is hidden there.
            title: 'Scheduled Delivery',
            description: 'Automatically send this report on a recurring schedule.',
            showIf: ({isSavable}) => isSavable === true,
            fields: [
                {
                    key: 'interval',
                    type: 'select',
                    label: 'Send frequency',
                    description: 'How often the report is emailed, around 1 AM site time. Daily covers the previous day, weekly the 7 days before, semi-monthly each half month, and monthly the previous month.',
                    options: Intervals.STANDARD_INTERVAL_OPTIONS,
                    fromContext: true,
                },
                {
                    key: 'weekday',
                    type: 'select',
                    label: 'Day to receive report',
                    description: 'The day the weekly report is emailed, around 1 AM site time. It covers the 7 days ending the day before.',
                    options: Intervals.WEEKDAY_OPTIONS,
                    showIf: ({interval}) => interval === Intervals.WEEKLY,
                },
            ],
        },
    ],
}
